<template>
  <div class="footer relative" data-test-root-footer>
    <div class="footer__wrapper" :class="{ 'footer__wrapper--loggedin': $auth.loggedIn }">
      <div class="footer__section">
        <div class="footer__column">
          <div class="footer__info-wrapper">
            <img
              class="footer__logo"
              src="./images/inverted-bravado-logo.svg"
              alt="Bravado logo"
              width="115"
              height="19"
              loading="lazy"
              decoding="async"
            />
            <div class="footer__info">
              Bravado is a community of trusted sales professionals endorsed by their customers.
            </div>

            <div class="footer__trust-pilot">
              <ClientOnly>
                <TrustPilotBadge />
              </ClientOnly>
            </div>

            <a
              :href="LINK_TO_MOBILE_APP"
              class="footer__download-button"
              rel="nofollow noreferrer noopener"
              target="_blank"
            >
              <img
                src="./images/apple-store.svg"
                alt="Apple Store logo"
                width="150"
                height="50"
                loading="lazy"
                decoding="async"
              />
            </a>
          </div>
        </div>

        <div class="footer__column">
          <div class="footer__title">Products</div>
          <NuxtLink :to="WAR_ROOM_POSTS_TAB_MYFEED_PAGE" class="footer__link">The War Room</NuxtLink>
          <NuxtLink :to="$auth.loggedIn ? JOBS_CANDIDATE_PROFILE_PAGE : JOBS_WELCOME_PAGE" class="footer__link">
            Jobs
          </NuxtLink>
          <a href="https://bravado.co/lp/techsalesu/" class="footer__link">Tech Sales U</a>
        </div>

        <div class="footer__column">
          <div class="footer__title">For employers</div>
          <a href="https://bravado.co/talent" class="footer__link">Hire sales talent</a>
          <a href="https://bravado.co/exec-search" class="footer__link">Executive search</a>
        </div>

        <div class="footer__column">
          <div class="footer__title">Company</div>
          <NuxtLink :to="ABOUT_US_PAGE" class="footer__link">About Us</NuxtLink>
          <a href="https://info.bravado.co/faq" class="footer__link" target="_blank">How It Works</a>
          <a href="https://bravado.co/blog/" class="footer__link" target="_blank">Blog</a>
          <a href="https://github.com/Bravado-network/BugBounty" class="footer__link" target="_blank">Bug Bounty</a>
        </div>

        <div class="footer__column">
          <div class="footer__title">Contact</div>
          <a href="https://bravado.co/press-events" class="footer__link" target="_blank">Press</a>
          <button class="footer__link footer__link-button" type="button" @click="openContactUs">Contact Bravado</button>
          <NuxtLink :to="CAREERS_PAGE" class="footer__link">Careers</NuxtLink>
        </div>
      </div>

      <div class="footer__add-on">
        <div class="footer__legal-right">&copy; {{ currentYear }} Bravado Inc. All rights reserved.</div>

        <div class="footer__addon-right">
          <div class="footer__addon-links">
            <button type="button" class="footer__link-button footer__link--inline" @click="handleOpenCookieSettings">
              Cookie Settings
            </button>
            <a href="https://info.bravado.co/privacy-policy" class="footer__link footer__link--inline" target="_blank">
              Privacy Policy
            </a>
            <a href="https://info.bravado.co/" class="footer__link footer__link--inline" target="_blank">
              Legal and Privacy Center
            </a>
            <a
              href="https://app.vanta.com/bravado.co/trust/8j5grqkla740zxky6oqe3"
              class="footer__link footer__link--inline"
              target="_blank"
            >
              Trust Center
            </a>
            <NuxtLink
              v-if="!$auth.loggedIn && $route.path === '/'"
              :to="{ name: $pages.SALES_DIRECTORY_PAGE.name, query: { prefix: 'A', page: '1' } }"
              class="footer__link footer__link--inline"
              data-test="sales-directory-link"
            >
              Sales Directory
            </NuxtLink>
            <a
              href="https://bravado.co/lp/jobs/open-roles-list"
              class="footer__link footer__link--inline"
              target="_blank"
            >
              Sales Jobs
            </a>
          </div>

          <div class="footer__icons">
            <SocialIconsBar class="footer__social" />
          </div>
        </div>
      </div>
    </div>
    <OrganizationJsonLd v-if="$route.path === '/'" />
  </div>
</template>

<script lang="ts" setup>
import { useContext } from '@nuxtjs/composition-api';
import OrganizationJsonLd from './OrganizationJsonLd.vue';
import {
  WAR_ROOM_POSTS_TAB_MYFEED_PAGE,
  JOBS_WELCOME_PAGE,
  JOBS_CANDIDATE_PROFILE_PAGE,
  ABOUT_US_PAGE,
  CAREERS_PAGE,
} from '@/constants';
import { LINK_TO_MOBILE_APP } from '~/constants/common';

const SocialIconsBar = () => import('@/components/common/SocialIconsBar/SocialIconsBar.vue');
const TrustPilotBadge = () => import('@/components/global/Badges/TrustPilotBadge.vue');

const { $modal, $sentryUtils } = useContext();

// We use button instead of link for a SEO reason - google will follow the link otherwise and spends a crawl budget
function openContactUs() {
  $modal.showWithRoute('CONTACT_US_MODAL');
}

function handleOpenCookieSettings() {
  // https://community.cookiepro.com/s/article/UUID-d8291f61-aa31-813a-ef16-3f6dec73d643?language=en_US
  try {
    // @ts-ignore
    window?.OneTrust?.ToggleInfoDisplay();
  } catch (e) {
    $sentryUtils.error(e);
  }
}

const currentYear = new Date().getFullYear();
</script>

<style scoped lang="scss">
$color: var(--gray-50);
$title-color: rgba(var(--white));

.footer {
  $this: &;

  background: var(--gray-90);
  width: 100%;

  &__wrapper {
    padding: 50px 24px;
    color: $color;
    // fixme: no such typography mixin

    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    max-width: calc(var(--page-max-width) - 48px);
    margin-left: auto;
    margin-right: auto;

    @include mq('sm') {
      padding: 38px;
    }

    @include mq('md') {
      padding: 65px 24px;
    }

    @include mq('lg') {
      padding: 65px 0;
    }

    &--loggedin {
      padding-bottom: 90px;
    }
  }

  &__logo {
    width: 115px;
    height: 19px;
  }

  &__column {
    & + & {
      margin-top: 36px;

      @include mq('sm') {
        margin-top: 0;
      }
    }
  }

  &__section {
    max-width: 1330px;
    width: 100%;
    margin: 0 auto 30px;
    align-items: start;
    padding-bottom: 36px;
    border-bottom: solid 1px var(--gray-80);

    @include mq('sm') {
      display: grid;
      grid-template-columns: 3fr 1fr 1fr 1fr 1fr;
      grid-gap: 2%;
    }

    &--inline {
      #{$this}__column {
        margin-top: 10px;

        @include mq('sm') {
          margin-top: 0;
        }
      }
    }

    & + & {
      margin-top: 30px;
    }
  }

  &__add-on {
    max-width: 1330px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-wrap: wrap;
    font-size: 13px;
    line-height: 14px;

    @include mq('sm') {
      display: grid;
      grid-template-columns: 35% 1fr;
      grid-gap: 2%;
      align-items: center;
    }
  }

  &__icons {
    line-height: 0;
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__title {
    text-transform: capitalize;
    font-weight: 600;
    font-size: 13px;
    color: $title-color;

    & + #{$this}__link {
      margin-top: 12px;
    }
  }

  &__info {
    max-width: 270px;
    margin-top: 10px;
    margin-bottom: 20px;
  }

  &__link-button {
    background: transparent;
    color: inherit;
    line-height: inherit;
    font-size: inherit;
    border: 0;
    padding: 0;
    margin: 0;
    cursor: pointer;
    text-align: left;
  }

  &__link-button,
  &__link {
    display: block;
    text-decoration: none;

    & + & {
      margin-top: 12px;
    }

    &--inline {
      display: inline-block;
      padding: 7px 0;

      & + & {
        margin-top: 0;
      }

      &::after {
        content: '•';
        display: inline-block;
        margin-left: 4px;
        margin-right: 4px;
      }

      &:last-child {
        &::after {
          display: none;
        }
      }
    }
  }

  &__copyright {
    font-size: 12px;
    line-height: 1.3;
    margin-top: 12px;
  }

  &__social {
    margin-left: -5px;
  }

  &__download-button {
    align-self: start;
  }

  &__download-button-image {
    width: 150px;
    object-fit: contain;
  }

  &__legal-right {
    display: inline-flex;
    height: 100%;
    align-items: center;
    margin-top: 14px;
    order: 1;

    @include mq('sm') {
      margin-top: auto;
      order: 0;
    }
  }

  &__addon-right {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  &__addon-links {
    margin-top: 26px;
    margin-right: 30px;
    padding: 10px 0;
    order: 1;

    @include mq('sm') {
      margin-top: 0;
      order: 0;
    }

    @include mq('sm', 'max') {
      width: 100%;
    }
  }

  &__trust-pilot {
    margin-bottom: 40px;
  }
}
</style>
